import {
  HISTORY_ACTION_LIMIT,
  WORKSPACE_DOCUMENT_VERSION,
  boundedText,
  cloneValue,
  collectionKey,
  compareIsoTimestamps,
  decodePayload,
  exactFields,
  isRecord,
  isoTimestamp,
  opaqueId,
  sourceRef,
  timestampRange,
} from "./preset-internals.js";

const DOCUMENT_FIELDS = Object.freeze([
  "collectionKey",
  "createdAt",
  "documentVersion",
  "headChangeId",
  "headRevisionId",
  "lastVisitedChildren",
  "revisions",
  "storeRevision",
  "updatedAt",
]);
const REVISION_FIELDS = Object.freeze([
  "actionId",
  "actionKind",
  "at",
  "basedOn",
  "changeId",
  "compacted",
  "parentRevisionId",
  "payload",
  "revisionId",
  "supersedesRevisionId",
]);
const LAST_VISITED_FIELDS = Object.freeze(["childRevisionId", "parentRevisionId"]);
const MAX_ACTION_KIND_LENGTH = 96;
const MAX_VISIBLE_HISTORY_NODES = HISTORY_ACTION_LIMIT + 2;

function invalid(reason) {
  return { kind: "invalid", reason };
}

export function canonicalWorkspaceActionKind(value = "payload-change") {
  return boundedText(value, "Preset action kind", { max: MAX_ACTION_KIND_LENGTH });
}

function decodeRevision(contract, raw) {
  if (!exactFields(raw, REVISION_FIELDS)) {
    throw new TypeError("Workspace revision fields must match v1.");
  }
  if (typeof raw.compacted !== "boolean") {
    throw new TypeError("Workspace compacted marker must be boolean.");
  }
  return {
    revisionId: opaqueId(raw.revisionId, "Workspace revision ID"),
    changeId: opaqueId(raw.changeId, "Workspace change ID"),
    parentRevisionId: raw.parentRevisionId === null
      ? null
      : opaqueId(raw.parentRevisionId, "Workspace parent revision ID"),
    supersedesRevisionId: raw.supersedesRevisionId === null
      ? null
      : opaqueId(raw.supersedesRevisionId, "Superseded workspace revision ID"),
    actionId: opaqueId(raw.actionId, "Workspace action ID"),
    actionKind: canonicalWorkspaceActionKind(raw.actionKind),
    at: isoTimestamp(raw.at, "Workspace revision time"),
    basedOn: sourceRef(raw.basedOn),
    compacted: raw.compacted,
    payload: decodePayload(contract, raw.payload),
  };
}

export function decodeWorkspaceDocument(raw, contract, expectedCollectionKey) {
  if (raw === null || raw === undefined) {
    return { kind: "missing", collectionKey: expectedCollectionKey };
  }
  if (!isRecord(raw)) {
    return invalid("workspace-document-must-be-an-object");
  }
  if (
    Number.isSafeInteger(raw.documentVersion)
    && raw.documentVersion > WORKSPACE_DOCUMENT_VERSION
  ) {
    return { kind: "unsupportedVersion", foundVersion: raw.documentVersion };
  }
  if (!exactFields(raw, DOCUMENT_FIELDS)) {
    return invalid("workspace-document-fields-must-match-v1");
  }
  if (raw.documentVersion !== WORKSPACE_DOCUMENT_VERSION) {
    return invalid("workspace-document-version-must-be-1");
  }

  let key;
  let storeRevision;
  let headRevisionId;
  let headChangeId;
  let createdAt;
  let updatedAt;
  try {
    key = collectionKey(raw.collectionKey);
    storeRevision = opaqueId(raw.storeRevision, "Workspace store revision");
    headRevisionId = opaqueId(raw.headRevisionId, "Workspace head revision ID");
    headChangeId = opaqueId(raw.headChangeId, "Workspace head change ID");
    ({ createdAt, updatedAt } = timestampRange(
      raw.createdAt,
      raw.updatedAt,
      "Workspace",
    ));
  } catch (_error) {
    return invalid("workspace-document-metadata-must-match-v1");
  }
  if (key !== expectedCollectionKey) {
    return invalid("workspace-document-collection-key-mismatch");
  }
  if (!Array.isArray(raw.revisions) || raw.revisions.length === 0) {
    return invalid("workspace-revisions-must-be-a-non-empty-array");
  }
  if (!Array.isArray(raw.lastVisitedChildren)) {
    return invalid("workspace-last-visited-children-must-be-an-array");
  }

  let revisions;
  try {
    revisions = raw.revisions.map((revision) => decodeRevision(contract, revision));
  } catch (_error) {
    return invalid("workspace-revisions-must-match-v1");
  }
  const revisionById = new Map();
  for (const revision of revisions) {
    if (revisionById.has(revision.revisionId)) {
      return invalid("workspace-revision-ids-must-be-unique");
    }
    revisionById.set(revision.revisionId, revision);
  }
  const head = revisionById.get(headRevisionId);
  if (!head || head.changeId !== headChangeId) {
    return invalid("workspace-head-must-reference-its-active-change");
  }
  for (const revision of revisions) {
    if (
      revision.parentRevisionId !== null
      && !revisionById.has(revision.parentRevisionId)
    ) {
      return invalid("workspace-parent-revision-must-exist");
    }
    if (
      revision.supersedesRevisionId !== null
      && !revisionById.has(revision.supersedesRevisionId)
    ) {
      return invalid("workspace-superseded-revision-must-exist");
    }
  }
  for (const revision of revisions) {
    if (
      revision.basedOn.kind !== "none"
      && !revisionById.has(revision.basedOn.revisionId)
    ) {
      return invalid("workspace-base-revision-must-exist");
    }
  }
  const visited = new Set();
  let cursor = head;
  while (cursor) {
    if (visited.has(cursor.revisionId)) {
      return invalid("workspace-active-history-must-be-acyclic");
    }
    visited.add(cursor.revisionId);
    cursor = cursor.parentRevisionId === null
      ? null
      : revisionById.get(cursor.parentRevisionId);
  }

  const lastVisitedChildren = [];
  const seenParents = new Set();
  try {
    for (const rawEntry of raw.lastVisitedChildren) {
      if (!exactFields(rawEntry, LAST_VISITED_FIELDS)) {
        throw new TypeError("Last-visited child fields must match v1.");
      }
      const entry = {
        parentRevisionId: opaqueId(rawEntry.parentRevisionId, "History parent revision ID"),
        childRevisionId: opaqueId(rawEntry.childRevisionId, "History child revision ID"),
      };
      const child = revisionById.get(entry.childRevisionId);
      if (
        seenParents.has(entry.parentRevisionId)
        || !revisionById.has(entry.parentRevisionId)
        || child?.parentRevisionId !== entry.parentRevisionId
      ) {
        throw new TypeError("Last-visited child relation is invalid.");
      }
      seenParents.add(entry.parentRevisionId);
      lastVisitedChildren.push(entry);
    }
  } catch (_error) {
    return invalid("workspace-last-visited-children-must-match-v1");
  }

  return {
    kind: "ready",
    documentVersion: WORKSPACE_DOCUMENT_VERSION,
    collectionKey: key,
    storeRevision,
    headRevisionId,
    headChangeId,
    revisions,
    lastVisitedChildren,
    createdAt,
    updatedAt,
  };
}

export function encodeWorkspaceDocument(state) {
  return {
    documentVersion: WORKSPACE_DOCUMENT_VERSION,
    collectionKey: state.collectionKey,
    storeRevision: state.storeRevision,
    headRevisionId: state.headRevisionId,
    headChangeId: state.headChangeId,
    revisions: state.revisions.map((revision) => cloneValue(revision)),
    lastVisitedChildren: state.lastVisitedChildren.map((entry) => cloneValue(entry)),
    createdAt: state.createdAt,
    updatedAt: state.updatedAt,
  };
}

export function workspaceRevisionMap(workspace) {
  return new Map(workspace.revisions.map((revision) => [revision.revisionId, revision]));
}

function supersededIds(workspace) {
  return new Set(
    workspace.revisions
      .map((revision) => revision.supersedesRevisionId)
      .filter(Boolean),
  );
}

export function activeWorkspaceChangeNodes(workspace) {
  return workspace.revisions.filter((revision) => revision.changeId === workspace.headChangeId);
}

export function workspaceChildRows(workspace, parentRevisionId) {
  const superseded = supersededIds(workspace);
  return activeWorkspaceChangeNodes(workspace).filter((revision) => (
    revision.parentRevisionId === parentRevisionId
    && !superseded.has(revision.revisionId)
  ));
}

function activePath(workspace) {
  const byId = workspaceRevisionMap(workspace);
  const path = [];
  let cursor = byId.get(workspace.headRevisionId) || null;
  while (cursor && cursor.changeId === workspace.headChangeId) {
    path.push(cursor);
    cursor = cursor.parentRevisionId === null ? null : byId.get(cursor.parentRevisionId);
  }
  return path.reverse();
}

export function compactWorkspaceHistory(workspace) {
  const candidates = activeWorkspaceChangeNodes(workspace)
    .filter((revision) => revision.actionKind !== "base")
    .sort((left, right) => compareIsoTimestamps(left.at, right.at));
  if (candidates.length <= HISTORY_ACTION_LIMIT) {
    return workspace;
  }
  const retained = new Set(
    candidates.slice(-HISTORY_ACTION_LIMIT).map((revision) => revision.revisionId),
  );
  retained.add(workspace.headRevisionId);
  for (const revision of workspace.revisions) {
    if (retained.has(revision.revisionId) && revision.basedOn.kind !== "none") {
      retained.add(revision.basedOn.revisionId);
    }
  }
  const retainedRevisions = workspace.revisions.filter((revision) => (
    revision.changeId !== workspace.headChangeId
    || retained.has(revision.revisionId)
  ));
  const retainedIds = new Set(retainedRevisions.map((revision) => revision.revisionId));
  workspace.revisions = retainedRevisions.map((revision) => {
    if (revision.changeId !== workspace.headChangeId) {
      return revision;
    }
    const parentRemoved = revision.parentRevisionId !== null
      && !retainedIds.has(revision.parentRevisionId);
    const supersededRemoved = revision.supersedesRevisionId !== null
      && !retainedIds.has(revision.supersedesRevisionId);
    if (!parentRemoved && !supersededRemoved) {
      return revision;
    }
    return {
      ...revision,
      parentRevisionId: parentRemoved ? null : revision.parentRevisionId,
      supersedesRevisionId: supersededRemoved ? null : revision.supersedesRevisionId,
      compacted: revision.compacted || parentRemoved,
    };
  });
  workspace.lastVisitedChildren = workspace.lastVisitedChildren.filter((entry) => (
    retainedIds.has(entry.parentRevisionId)
    && retainedIds.has(entry.childRevisionId)
  ));
  return workspace;
}

export function projectWorkspaceHistory(workspace, contract) {
  const superseded = supersededIds(workspace);
  const byId = workspaceRevisionMap(workspace);
  const activeIds = new Set(activePath(workspace).map((revision) => revision.revisionId));
  const visible = activeWorkspaceChangeNodes(workspace)
    .filter((revision) => (
      revision.actionKind !== "base"
      && !superseded.has(revision.revisionId)
    ))
    .sort((left, right) => compareIsoTimestamps(left.at, right.at));
  let rows = visible;
  let compactedCount = 0;
  if (visible.length > MAX_VISIBLE_HISTORY_NODES) {
    compactedCount = visible.length - MAX_VISIBLE_HISTORY_NODES;
    rows = visible.slice(-MAX_VISIBLE_HISTORY_NODES);
  }
  const projectedRevisionIds = new Set(rows.map((revision) => revision.revisionId));
  const nodes = rows.map((revision, rowIndex) => {
    const parent = revision.parentRevisionId ? byId.get(revision.parentRevisionId) || null : null;
    let summary = revision.actionKind;
    if (typeof contract.describeRevision === "function") {
      const described = contract.describeRevision(cloneValue(revision), cloneValue(parent));
      if (typeof described === "string" && described.trim()) {
        summary = described.trim();
      }
    }
    return {
      revisionId: revision.revisionId,
      changeId: revision.changeId,
      parentRevisionId: revision.parentRevisionId,
      childRevisionIds: workspaceChildRows(workspace, revision.revisionId)
        .map((child) => child.revisionId),
      actionId: revision.actionId,
      actionKind: revision.actionKind,
      at: revision.at,
      basedOn: cloneValue(revision.basedOn),
      parentPayload: parent && !projectedRevisionIds.has(parent.revisionId)
        ? cloneValue(parent.payload)
        : null,
      compacted: revision.compacted || (compactedCount > 0 && rowIndex === 0),
      current: revision.revisionId === workspace.headRevisionId,
      activePath: activeIds.has(revision.revisionId),
      summary,
      payload: cloneValue(revision.payload),
    };
  });
  const head = byId.get(workspace.headRevisionId);
  const redoChoices = workspaceChildRows(workspace, workspace.headRevisionId)
    .map((revision) => ({
      revisionId: revision.revisionId,
      actionId: revision.actionId,
      actionKind: revision.actionKind,
      at: revision.at,
    }));
  return {
    nodes,
    compactedCount,
    canUndo: Boolean(head?.parentRevisionId),
    canRedo: redoChoices.length > 0,
    redoChoices,
  };
}
